import { UseFormReturn } from "react-hook-form"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useConsultationReportStoreContext } from "@/modules/patients/detail/consultation-report/store/useConsultationReportStore"
import {
  ACTUAL_POINT_OF_CARE_FIELD,
  isNotForDoctorVisit,
  MEDICAL_POINT_OF_CARE_FIELD,
  SHOULD_VALIDATE_AND_DIRTY,
} from "@/modules/patients/detail/progress-note/point-of-care/constants"
import { useTelecareFollowUpSuggestion } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useTelecareFollowUpSuggestion"
import { useUpdateReferralState } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useUpdateReferralState"
import { usePointOfCareContext } from "@/modules/patients/detail/progress-note/point-of-care/point-of-care-context"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { Country, PointOfCareType, TimeToTreatType } from "@/types/__generated__/union-types"

type UseSelectPointOfCareParameters = {
  form: UseFormReturn<PointOfCareFormType>
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  patientId: string
  countryCode: Country
  clearBetterDocSuggestion: () => void
  preselectCareProviderForPointOfCare: (selectedPointOfCare: PointOfCareType | undefined) => void
}

export const useSelectPointOfCare = ({
  form,
  episodeOfCareProgressNoteData,
  patientId,
  countryCode,
  clearBetterDocSuggestion,
  preselectCareProviderForPointOfCare,
}: UseSelectPointOfCareParameters) => {
  const { pointOfCare } = episodeOfCareProgressNoteData
  const { setCareProvider } = useUpdateReferralState(pointOfCare)
  const { triggerSuggestionIfApplicable } = useTelecareFollowUpSuggestion({
    progressNote: episodeOfCareProgressNoteData,
    countryCode,
    patientId,
  })
  const setConsultationReportTimeToTreat = useConsultationReportStoreContext(
    (state) => state.setConsultationReportTimeToTreat,
  )
  const { timeToTreatByPointOfCare } = usePointOfCareContext()
  const { setValue, getValues } = form

  const getDefaultTimeToTreatType = (): TimeToTreatType | null => {
    const effectivePointOfCare = getValues(ACTUAL_POINT_OF_CARE_FIELD) || getValues(MEDICAL_POINT_OF_CARE_FIELD)
    return effectivePointOfCare === "SPECIALIST" ? (timeToTreatByPointOfCare.SPECIALIST[0]?.value ?? null) : null
  }

  const resetOptionalFields = (medicalValue: PointOfCareType | null): void => {
    if (medicalValue) setValue(MEDICAL_POINT_OF_CARE_FIELD, medicalValue, SHOULD_VALIDATE_AND_DIRTY)
    setValue("timeToTreatType", getDefaultTimeToTreatType(), SHOULD_VALIDATE_AND_DIRTY)
    setValue("purpose", [], SHOULD_VALIDATE_AND_DIRTY)
    setValue("reasonForChoosingType", null, SHOULD_VALIDATE_AND_DIRTY)
    setValue("isDoctorVisitAvoided", isNotForDoctorVisit(medicalValue) ? null : false, SHOULD_VALIDATE_AND_DIRTY)
    setValue("careProviderId", null, SHOULD_VALIDATE_AND_DIRTY)
    setValue("specialtyId", null, SHOULD_VALIDATE_AND_DIRTY)
    setValue("resolvedBetterDocUsage", null, SHOULD_VALIDATE_AND_DIRTY)
    setConsultationReportTimeToTreat(null)
  }

  const onMedicalPointOfCareSelect = (value: PointOfCareType | undefined): void => {
    if (value === undefined || value === getValues(MEDICAL_POINT_OF_CARE_FIELD)) return
    form.clearErrors()
    clearBetterDocSuggestion()
    resetOptionalFields(value)
    setCareProvider(null)
    preselectCareProviderForPointOfCare(value)
    FaroInsightsWrapper.pushEvent("point_of_care_selection_medical")
    void triggerSuggestionIfApplicable(value)
  }

  const onActualPointOfCareSelect = (value: PointOfCareType | undefined): void => {
    if (value === getValues(ACTUAL_POINT_OF_CARE_FIELD)) return
    form.clearErrors()
    clearBetterDocSuggestion()
    setValue(ACTUAL_POINT_OF_CARE_FIELD, value, SHOULD_VALIDATE_AND_DIRTY)
    resetOptionalFields(getValues(MEDICAL_POINT_OF_CARE_FIELD) || null)
    setValue("isDoctorVisitAvoided", isNotForDoctorVisit(value) ? null : false)
    setCareProvider(null)
    preselectCareProviderForPointOfCare(value)
    FaroInsightsWrapper.pushEvent("point_of_care_selection_actual")
    void triggerSuggestionIfApplicable(value)
  }

  return { onMedicalPointOfCareSelect, onActualPointOfCareSelect, setCareProvider }
}
